// Local Access Keys — operator settings screen (production manager).
//
// Mint / list / revoke the local-access keys a LOCAL manager box uses to reach
// the remote shared store. The raw key is shown ONCE at creation in a copyable
// box with a "won't be shown again" warning. Each existing key row exposes a
// Revoke (confirm-gated) action and a Rotate action (revoke old + create new,
// surfacing the new raw key).

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useLocalKeys, useCreateLocalKey, useRevokeLocalKey } from './hooks';
import { QueryState } from '../../components/QueryState';
import type { LocalAccessKey, CreateKeyResponse } from '../../lib/types';

const ID = 'em-mgr--keys';

const FIELD_CLS =
  'w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 ' +
  'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100';
const BTN_PRIMARY =
  'rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 ' +
  'disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300';

function formatTs(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Copy-to-clipboard button; falls back silently when the clipboard API is unavailable. */
function CopyButton({ id, value }: { id: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
    } catch {
      /* clipboard unavailable (e.g. insecure context) — no-op, operator can select manually */
    }
  }
  return (
    <button
      type="button"
      data-id={id}
      onClick={() => void copy()}
      className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/** The one-time raw-key reveal box shown after a create/rotate. */
function RawKeyReveal({
  keyValue,
  label,
  onDismiss,
}: {
  keyValue: string;
  label: string;
  onDismiss: () => void;
}) {
  return (
    <div
      data-id={`${ID}--raw`}
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/40"
    >
      <p
        data-id={`${ID}--raw--warning`}
        className="text-sm font-medium text-amber-800 dark:text-amber-300"
      >
        Copy this key now — it won’t be shown again.
      </p>
      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
        Key for “{label}”. Paste it into the local box’s Key Gate to connect it to the shared store.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <code
          data-id={`${ID}--raw--value`}
          className="block flex-1 break-all rounded bg-white px-2 py-1.5 font-mono text-sm text-slate-900 dark:bg-slate-900 dark:text-slate-100"
        >
          {keyValue}
        </code>
        <CopyButton id={`${ID}--raw--copy`} value={keyValue} />
      </div>
      <button
        type="button"
        data-id={`${ID}--raw--dismiss`}
        onClick={onDismiss}
        className="mt-3 text-xs font-medium text-amber-800 underline hover:text-amber-900 dark:text-amber-300"
      >
        I’ve copied it — dismiss
      </button>
    </div>
  );
}

interface RowActionsProps {
  row: LocalAccessKey;
  onRevoke: (id: string) => void;
  onRotate: (row: LocalAccessKey) => void;
  busy: boolean;
  /** True when THIS row's revoke confirmation is the one open (from the URL). */
  confirming?: boolean;
  /** Open/close the confirmation. Omitted → local state (unit tests). */
  onConfirmingChange?: (keyId: string | null) => void;
}

/** Revoke (confirm-gated) + Rotate for one row; confirm state is routed (?confirmRevoke={id})
 * so it survives a refresh, and the raw key is never in the URL. */
function RowActions({ row, onRevoke, onRotate, busy, confirming: routeConfirming, onConfirmingChange }: RowActionsProps) {
  const [localConfirming, setLocalConfirming] = useState(false);
  const confirming = onConfirmingChange ? routeConfirming === true : localConfirming;
  const setConfirming = (next: boolean) =>
    onConfirmingChange ? onConfirmingChange(next ? row.id : null) : setLocalConfirming(next);
  const revoked = row.revokedAt !== null;

  if (revoked) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  if (confirming) {
    return (
      <span className="flex items-center gap-2">
        <span className="text-xs text-slate-500 dark:text-slate-400">Revoke?</span>
        <button
          type="button"
          data-id={`${ID}--row--revoke-confirm`}
          disabled={busy}
          onClick={() => {
            setConfirming(false);
            onRevoke(row.id);
          }}
          className="rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
        >
          Yes, revoke
        </button>
        <button
          type="button"
          data-id={`${ID}--row--revoke-cancel`}
          onClick={() => setConfirming(false)}
          className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 dark:border-slate-600 dark:text-slate-200"
        >
          Cancel
        </button>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <button
        type="button"
        data-id={`${ID}--row--rotate`}
        disabled={busy}
        onClick={() => onRotate(row)}
        className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        Rotate
      </button>
      <button
        type="button"
        data-id={`${ID}--row--revoke`}
        disabled={busy}
        onClick={() => setConfirming(true)}
        className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/40"
      >
        Revoke
      </button>
    </span>
  );
}

export interface LocalKeysScreenProps {
  /** Key id whose revoke confirmation is open (from ?confirmRevoke=). */
  confirmRevoke?: string | null;
  /** Open/close a confirmation. Omitted → rows keep local state (unit tests). */
  onConfirmRevokeChange?: (keyId: string | null) => void;
}

export function LocalKeysScreen({
  confirmRevoke = null,
  onConfirmRevokeChange,
}: LocalKeysScreenProps = {}) {
  const keys = useLocalKeys();
  const createKey = useCreateLocalKey();
  const revokeKey = useRevokeLocalKey();

  const [label, setLabel] = useState('');
  // The most recently minted raw key (from create OR rotate), shown once.
  const [reveal, setReveal] = useState<CreateKeyResponse | null>(null);

  const busy = createKey.isPending || revokeKey.isPending;

  function submitCreate(e: FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    createKey.mutate(label.trim(), {
      onSuccess: (data) => {
        setReveal(data);
        setLabel('');
      },
    });
  }

  function handleRevoke(id: string) {
    revokeKey.mutate(id);
  }

  // Rotate = revoke the old key, then mint a replacement carrying the same label;
  // surface the new raw key in the reveal box.
  function handleRotate(row: LocalAccessKey) {
    revokeKey.mutate(row.id, {
      onSuccess: () => {
        createKey.mutate(row.label, {
          onSuccess: (data) => setReveal(data),
        });
      },
    });
  }

  return (
    <section data-id={ID} className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Local Access Keys</h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
          Keys a local manager box uses to reach this remote shared store. Create one here, then
          paste it into the local box’s Key Gate. The raw key is shown only once.
        </p>
      </div>

      <form
        onSubmit={submitCreate}
        data-id={`${ID}--create`}
        className="flex flex-wrap items-end gap-3 rounded border border-slate-200 p-3 dark:border-slate-800"
      >
        <label className="flex-1 text-sm">
          <span className="text-slate-500">Label</span>
          <input
            data-id={`${ID}--create--label`}
            className={FIELD_CLS}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. terran-laptop"
            required
          />
        </label>
        <button
          type="submit"
          data-id={`${ID}--create--submit`}
          disabled={busy}
          className={BTN_PRIMARY}
        >
          {createKey.isPending ? 'Creating…' : 'Create key'}
        </button>
        {createKey.isError && (
          <span data-id={`${ID}--create--error`} className="text-sm text-red-600 dark:text-red-400">
            {createKey.error instanceof Error ? createKey.error.message : 'Create failed'}
          </span>
        )}
      </form>

      {reveal && (
        <RawKeyReveal
          keyValue={reveal.key}
          label={reveal.label}
          onDismiss={() => setReveal(null)}
        />
      )}

      <QueryState
        idBase={`${ID}--list`}
        isPending={keys.isPending}
        isError={keys.isError}
        error={keys.error}
        data={keys.data}
      >
        {(data) =>
          data.keys.length === 0 ? (
            <div
              data-id={`${ID}--empty`}
              className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-500 dark:border-slate-700"
            >
              No keys yet. Create one above to connect a local box.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
              <table data-id={`${ID}--table`} className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
                  <tr>
                    <th className="px-3 py-2 font-medium">Label</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                    <th className="px-3 py-2 font-medium">Last used</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.keys.map((row) => (
                    <tr
                      key={row.id}
                      data-id={`${ID}--row`}
                      className="border-t border-slate-200 dark:border-slate-700"
                    >
                      <td className="px-3 py-2 font-medium">{row.label}</td>
                      <td className="px-3 py-2 text-slate-500">{formatTs(row.createdAt)}</td>
                      <td className="px-3 py-2 text-slate-500">{formatTs(row.lastUsedAt)}</td>
                      <td className="px-3 py-2">
                        {row.revokedAt ? (
                          <span
                            data-id={`${ID}--row--status-revoked`}
                            className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                          >
                            revoked
                          </span>
                        ) : (
                          <span
                            data-id={`${ID}--row--status-active`}
                            className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300"
                          >
                            active
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <RowActions
                          row={row}
                          onRevoke={handleRevoke}
                          onRotate={handleRotate}
                          busy={busy}
                          confirming={confirmRevoke === row.id}
                          onConfirmingChange={onConfirmRevokeChange}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QueryState>
    </section>
  );
}
